import { Sparkline } from '@mantine/charts';
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Divider,
  Flex,
  Group,
  Loader,
  Modal,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Tooltip,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconChartLine, IconPlayerPlay, IconRefresh } from '@tabler/icons-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import { formatDate } from './shared';

const TOOLTIPS = {
  s5: 'Success@5 — share of gold questions whose correct unit appears in the top 5 results.',
  mrr: 'Mean reciprocal rank of the first correct unit — 1.0 means it always ranks first.',
  snapshot:
    'State of the corpus when the run started: published docs · embedded units. Dangling anchors = gold questions pointing at units that no longer exist.',
  dangling:
    'Gold-set questions whose anchor units were deleted or re-chunked away — they count as automatic misses. Fix the gold set or restore the units.',
};

const PIPELINES = ['kbHybrid', 'kbKnn', 'kbBm25'];

// A backend eval job that dies after accepting the POST never produces a run id, so its
// optimistic row would wait — and keep the poll below hitting /evalRuns — for as long as
// the tab stays open. Past this ceiling the row says so and stops driving the poll.
const PENDING_RUN_TIMEOUT_MS = 10 * 60 * 1000;

const capsStyle = { letterSpacing: '0.04em' };

const thStyle = {
  fontSize: 11,
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: 'var(--mantine-color-dimmed)',
};

const fmtMetric = (value, digits = 3) => {
  const num = parseFloat(value);
  return Number.isFinite(num) ? num.toFixed(digits) : '—';
};

// Both halves route through fmtMetric so a missing metric reads '—' rather than 'NaN',
// and a null recall doesn't render as a convincing '0.00'.
const fmtLocaleMetrics = (perLocale, locale) => {
  const metrics = perLocale?.[locale];
  if (!metrics) return '—';
  return `${fmtMetric(metrics.recall, 2)} / ${fmtMetric(metrics.mrr, 2)}`;
};

const corpusSummary = (corpusState) =>
  corpusState ? `${corpusState.publishedDocuments} docs · ${corpusState.embeddedUnits} units` : '—';

const HeaderTooltip = ({ label, children }) => (
  <Tooltip label={label} multiline maw={260} withArrow>
    <span style={{ cursor: 'help' }}>{children}</span>
  </Tooltip>
);

const SnapshotStat = ({ value, label, warning, tooltip }) => {
  const stat = (
    <div style={warning ? { cursor: 'help' } : undefined}>
      <Text fz={20} fw={600} c={warning ? 'warning' : undefined}>
        {value}
      </Text>
      <Text fz={12} c="dimmed">
        {label}
      </Text>
    </div>
  );
  if (!warning) return stat;
  return (
    <Tooltip label={tooltip} multiline maw={260} withArrow>
      {stat}
    </Tooltip>
  );
};

const EvaluationsScreen = ({ scope }) => {
  const fetch = useFetch();
  const { domainId } = useParams();

  const [runs, setRuns] = useState();
  const [error, setError] = useState();
  const [pendingRuns, setPendingRuns] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ pipeline: 'kbHybrid', corpus: 'client', locale: 'all', notes: '' });
  const pendingSeq = useRef(0);

  const load = useCallback(() => {
    return fetch('/api/admin/knowledge/evalRuns')
      .then(({ runs: fetched }) => {
        setRuns(fetched || []);
        setError(undefined);
        return fetched || [];
      })
      .catch((e) => {
        setError(e);
        return undefined;
      });
  }, [fetch]);

  useEffect(() => {
    load();
  }, [load]);

  const refresh = useCallback(() => {
    load().then((fetched) => {
      if (!fetched) return;
      // Each pending row clears when a run it didn't see at POST time lands. Concurrent
      // runs share no state, so we consume one fresh run per pending (oldest first) —
      // one finishing never drops another that's still running.
      setPendingRuns((prev) => {
        const consumed = new Set();
        const byOldest = [...prev].sort((a, b) => a.startedAt.localeCompare(b.startedAt));
        const survivors = byOldest
          .filter((pending) => {
            const match = fetched.find((run) => !consumed.has(run.id) && !pending.baselineIds.has(run.id));
            if (match) {
              consumed.add(match.id);
              return false;
            }
            return true;
          })
          .map((pending) =>
            pending.timedOut || Date.now() - new Date(pending.startedAt).getTime() < PENDING_RUN_TIMEOUT_MS
              ? pending
              : { ...pending, timedOut: true }
          );
        return survivors.sort((a, b) => b.startedAt.localeCompare(a.startedAt));
      });
    });
  }, [load]);

  // Poll while runs are in flight so finished rows fill in without a manual refresh.
  // Timed-out rows stay visible but stop counting, so a dead job can't poll forever.
  const awaitingCount = pendingRuns.filter((pending) => !pending.timedOut).length;
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  useEffect(() => {
    if (awaitingCount === 0) return undefined;
    const timer = setInterval(() => refreshRef.current(), 5000);
    return () => clearInterval(timer);
  }, [awaitingCount]);

  // The system mount (/superAdmin) has no :domainId in the route, so a client-corpus
  // run has nothing to run against — offer system only there.
  const canRunClientCorpus = !!domainId;

  const openModal = () => {
    setForm({
      pipeline: 'kbHybrid',
      corpus: canRunClientCorpus && scope !== 'system' ? 'client' : 'system',
      locale: 'all',
      notes: '',
    });
    setModalOpen(true);
  };

  const setField = (field) => (value) => setForm((prev) => ({ ...prev, [field]: value }));

  const startRun = () => {
    // Belt and braces: JSON.stringify drops an undefined domainId silently, so the
    // POST would look valid while running against nothing.
    if (form.corpus === 'client' && !domainId) return;
    setSubmitting(true);
    const body = { pipeline: form.pipeline, corpus: form.corpus };
    if (form.corpus === 'client') body.domainId = domainId;
    if (form.locale !== 'all') body.locale = form.locale;
    if (form.notes.trim()) body.notes = form.notes.trim();
    fetch('/api/admin/knowledge/evalRuns', { method: 'POST', body: JSON.stringify(body) })
      .then(() => {
        pendingSeq.current += 1;
        setPendingRuns((prev) => [
          {
            localId: pendingSeq.current,
            pipeline: form.pipeline,
            corpus: form.corpus,
            startedAt: new Date().toISOString(),
            // Runs that already existed at POST time — a new run means this one finished.
            baselineIds: new Set((runs || []).map((run) => run.id)),
          },
          ...prev,
        ]);
        setModalOpen(false);
        notifications.show({
          title: 'Evaluation started',
          message: `Running ${form.pipeline} against the gold set — refresh to see the finished row.`,
          color: 'success',
        });
      })
      .catch((e) => {
        notifications.show({ title: 'Could not start evaluation', message: e.message, color: 'danger' });
      })
      .finally(() => setSubmitting(false));
  };

  if (runs === undefined) {
    // useFetch rejects with the parsed JSON error body, which doesn't always carry
    // `.message` — without the fallback ErrorMessage renders nothing at all.
    return error ? <ErrorMessage message={error.message || 'Could not load evaluation runs.'} /> : <Spinner />;
  }

  const hybridRuns = runs.filter((run) => run.config?.pipeline === 'kbHybrid' && Number.isFinite(parseFloat(run.mrr)));
  const sparkData = hybridRuns.map((run) => parseFloat(run.mrr)).reverse();
  const latestMrr = hybridRuns[0] ? parseFloat(hybridRuns[0].mrr) : null;
  const previousMrr = hybridRuns[1] ? parseFloat(hybridRuns[1].mrr) : null;
  const delta = latestMrr != null && previousMrr != null ? latestMrr - previousMrr : null;
  const snapshot = runs.find((run) => run.config?.corpusState)?.config.corpusState;

  return (
    <div>
      {error && (
        <ErrorMessage message={error.message || 'Could not load evaluation runs.'} style={{ marginBottom: 8 }} />
      )}

      <Flex gap={10} align="stretch" wrap="wrap">
        <Card withBorder radius="sm" padding="md" w={340}>
          <Flex justify="space-between" align="flex-start">
            <div>
              <Text fz={11} c="dimmed" tt="uppercase" style={capsStyle}>
                MRR · kbHybrid
              </Text>
              <Text fz={24} fw={600} mt={2}>
                {latestMrr != null ? latestMrr.toFixed(3) : '—'}
              </Text>
              {delta != null && (
                <Text fz={12} fw={500} c={delta >= 0 ? 'success' : 'danger'}>
                  {delta >= 0 ? '+' : ''}
                  {delta.toFixed(3)} vs previous run
                </Text>
              )}
            </div>
            {sparkData.length >= 2 && (
              <Box w={150}>
                <Sparkline data={sparkData} h={44} color="primary.6" curveType="linear" fillOpacity={0.18} />
              </Box>
            )}
          </Flex>
        </Card>

        <Card withBorder radius="sm" padding="md" style={{ flex: 1, minWidth: 260 }}>
          <Text fz={11} c="dimmed" tt="uppercase" style={capsStyle}>
            Corpus snapshot
          </Text>
          <Flex gap={28} mt={8} wrap="wrap">
            <SnapshotStat value={snapshot ? snapshot.publishedDocuments : '—'} label="published docs" />
            <SnapshotStat value={snapshot ? snapshot.embeddedUnits : '—'} label="embedded units" />
            <SnapshotStat
              value={snapshot ? snapshot.danglingGoldUnits : '—'}
              label="dangling gold anchors"
              warning={snapshot?.danglingGoldUnits > 0}
              tooltip={TOOLTIPS.dangling}
            />
          </Flex>
        </Card>

        <Group align="flex-start" gap="xs">
          <Button leftSection={<IconPlayerPlay size={15} stroke={1.5} />} onClick={openModal}>
            Run evaluation
          </Button>
          {pendingRuns.length > 0 && (
            <Tooltip label="Refresh runs" withArrow>
              <ActionIcon variant="default" size="lg" onClick={refresh} aria-label="Refresh runs">
                <IconRefresh size={16} stroke={1.5} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Flex>

      <Card withBorder radius="sm" padding="xs" mt={12}>
        {runs.length === 0 && pendingRuns.length === 0 ? (
          <Center py={60}>
            <Stack align="center" gap="xs">
              <IconChartLine size={44} stroke={1} color="var(--mantine-color-dimmed)" />
              <Text fw={500}>No evaluation runs yet</Text>
              <Text size="sm" c="dimmed">
                Run an evaluation to score retrieval against the gold question set.
              </Text>
            </Stack>
          </Center>
        ) : (
          <Table.ScrollContainer minWidth={960}>
            <Table verticalSpacing="sm" horizontalSpacing="sm" styles={{ th: thStyle }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Run</Table.Th>
                  <Table.Th>Pipeline</Table.Th>
                  <Table.Th>
                    <HeaderTooltip label={TOOLTIPS.s5}>s@5</HeaderTooltip>
                  </Table.Th>
                  <Table.Th>
                    <HeaderTooltip label={TOOLTIPS.mrr}>MRR</HeaderTooltip>
                  </Table.Th>
                  <Table.Th>bg</Table.Th>
                  <Table.Th>en</Table.Th>
                  <Table.Th>
                    <HeaderTooltip label={TOOLTIPS.snapshot}>Corpus snapshot</HeaderTooltip>
                  </Table.Th>
                  <Table.Th>Notes</Table.Th>
                  <Table.Th>Date</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {pendingRuns.map((pending) => (
                  <Table.Tr key={`pending-${pending.localId}`}>
                    <Table.Td>
                      <Text fz={12} ff="monospace">
                        —
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="default" size="sm" tt="none">
                        {pending.pipeline}
                      </Badge>
                    </Table.Td>
                    <Table.Td colSpan={7}>
                      <Group gap="xs">
                        {!pending.timedOut && <Loader size="xs" type="oval" />}
                        <Text fz={13} c={pending.timedOut ? 'warning' : 'dimmed'}>
                          {pending.timedOut ? 'no result — the run never reported back' : 'running…'} ·{' '}
                          {pending.corpus} corpus · started {formatDate(pending.startedAt)}
                        </Text>
                      </Group>
                    </Table.Td>
                  </Table.Tr>
                ))}
                {runs.map((run) => (
                  <Table.Tr key={run.id}>
                    <Table.Td>
                      <Text fz={12} ff="monospace">
                        {run.id}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="default" size="sm" tt="none">
                        {run.config?.pipeline}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text fz={13} fw={600}>
                        {fmtMetric(run.recallAt5)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz={13} fw={600}>
                        {fmtMetric(run.mrr)}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz={13}>{fmtLocaleMetrics(run.perLocale, 'bg')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text fz={13}>{fmtLocaleMetrics(run.perLocale, 'en')}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Group gap={6} wrap="wrap">
                        <Text fz={12}>{corpusSummary(run.config?.corpusState)}</Text>
                        {run.config?.corpusState?.danglingGoldUnits > 0 && (
                          <Tooltip label={TOOLTIPS.dangling} multiline maw={260} withArrow>
                            <Badge variant="light" color="warning" size="sm" tt="none">
                              {run.config.corpusState.danglingGoldUnits} dangling anchors
                            </Badge>
                          </Tooltip>
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      {run.notes ? (
                        <Tooltip label={run.notes} multiline maw={260} withArrow>
                          <Text fz={12} c="dimmed" truncate maw={220}>
                            {run.notes}
                          </Text>
                        </Tooltip>
                      ) : (
                        <Text fz={12} c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text fz={12} c="dimmed" style={{ whiteSpace: 'nowrap' }}>
                        {formatDate(run.createdAt)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title="Run evaluation" size="md" centered>
        <div style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '14px 12px', alignItems: 'center' }}>
          <Text size="sm" fw={500}>
            Pipeline
          </Text>
          <Select
            data={PIPELINES}
            value={form.pipeline}
            onChange={(value) => setField('pipeline')(value || 'kbHybrid')}
            allowDeselect={false}
            w={180}
          />
          <Text size="sm" fw={500}>
            Corpus
          </Text>
          <div>
            <Select
              data={[
                { value: 'client', label: 'Client corpus', disabled: !canRunClientCorpus },
                { value: 'system', label: 'System corpus' },
              ]}
              value={form.corpus}
              onChange={(value) => setField('corpus')(value || 'system')}
              allowDeselect={false}
              w={220}
            />
            {!canRunClientCorpus && (
              <Text fz={12} c="dimmed" mt={4}>
                Client-corpus runs need a domain — start one from that domain’s Chat Bot → Knowledge Base tab.
              </Text>
            )}
          </div>
          <Text size="sm" fw={500}>
            Locale
          </Text>
          <Select
            data={[
              { value: 'all', label: 'All locales' },
              { value: 'bg', label: 'bg' },
              { value: 'en', label: 'en' },
            ]}
            value={form.locale}
            onChange={(value) => setField('locale')(value || 'all')}
            allowDeselect={false}
            w={160}
          />
          <Text size="sm" fw={500}>
            Notes
          </Text>
          <TextInput
            placeholder="optional"
            value={form.notes}
            onChange={(e) => setField('notes')(e.currentTarget.value)}
          />
        </div>
        <Text fz={12} c="dimmed" mt={10}>
          Runs asynchronously against the gold question set — the row appears as “running…” and fills in on refresh.
        </Text>
        <Divider my="md" />
        <Group justify="flex-end" gap="xs">
          <Button variant="default" onClick={() => setModalOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={startRun}
            loading={submitting}
            disabled={form.corpus === 'client' && !canRunClientCorpus}
            leftSection={<IconPlayerPlay size={15} stroke={1.5} />}
          >
            Run evaluation
          </Button>
        </Group>
      </Modal>
    </div>
  );
};

export default EvaluationsScreen;
